<template>
    <DashboardCard
        v-if="loading || showError || leaderboard?.length > 1"
        :title="title"
        :icon="icon"
        :show-footer="false"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template #contentNoPadding>
            <DataTable
                class="fm-table"
                :value="!loading ? rankedLeaderboard : loadingRows"
                scrollable
                :scroll-height="tableScrollHeight"
                :style="{ minHeight: tableScrollHeight }"
                :pt="dataTablePt"
            >
                <LeaderboardTableColumn
                    key="rank"
                    field="rank"
                    header="Rank"
                    :loading="loading"
                    loading-width="28px"
                    centred
                />
                <LeaderboardTableColumn
                    key="displayName"
                    field="displayName"
                    :header="dataLabel"
                    extra-class="display-name"
                    :loading="loading"
                />
                <LeaderboardTableColumn
                    v-if="showResponsesColumn"
                    key="responded"
                    field="responded"
                    header="Responses"
                    extra-class="responses"
                    loading-width="40px"
                    centred
                    :loading="loading"
                />
                <LeaderboardTableColumn
                    key="metric"
                    field="nps"
                    :header="metric"
                    extra-class="score"
                    loading-width="80px"
                    centred
                    :loading="loading"
                >
                    <template #body="slotProps">
                        <div
                            :aria-labelledby="`fm-table-score-desc-${slotProps.data.rank}`"
                            class="fm-table__column--score"
                        >
                            <span
                                :id="`fm-table-score-desc-${slotProps.data.rank}`"
                                class="screen-reader-only"
                            >
                                {{
                                    getAriaScoreDesc(
                                        Number(slotProps.data.nps),
                                        slotProps.data.change,
                                        timePeriod,
                                        metric
                                    )
                                }}
                            </span>
                            <div
                                class="fm-table__column--score__current"
                                aria-hidden="true"
                            >
                                {{
                                    formatScoreWithPercentage(
                                        Number(slotProps.data.nps),
                                        isFiveScore,
                                        csatAsPercent,
                                        metric,
                                        decimals
                                    )
                                }}
                            </div>
                            <div
                                :class="[
                                    'fm-table__column--score__change',
                                    `fm-table__column--score__change--${getDiffVariant(Number(slotProps.data.change))}`,
                                ]"
                                aria-hidden="true"
                            >
                                <FontAwesomeIcon
                                    class="fm-table__column--score__change__icon"
                                    :icon="
                                        getDiffIcon(
                                            Number(slotProps.data.change)
                                        )
                                    "
                                />
                                <span
                                    class="fm-table__column--score__change__number"
                                >
                                    {{
                                        formatScoreWithPercentage(
                                            Number(slotProps.data.change),
                                            isFiveScore,
                                            csatAsPercent,
                                            metric,
                                            decimals
                                        )
                                    }}
                                </span>
                            </div>
                        </div>
                    </template>
                </LeaderboardTableColumn>
            </DataTable>
        </template>
    </DashboardCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { type IconDefinition } from 'fontawesome/pro-regular-svg-icons'
import DataTable from 'primevue/datatable'
import {
    getAriaScoreDesc,
    getDiffIcon,
    getDiffVariant,
} from '@/utils/frontlineManagerDash'
import { formatScoreWithPercentage } from '@/utils/number'
import { tabletBreakpoint } from '@/utils/breakpoints'
import { ILeaderboardResult } from '@/entities/leaderboard'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import LeaderboardTableColumn from '@/components/FrontlineManagerDash/LeaderboardTableColumn.vue'

interface Props {
    title: string
    dataLabel: string
    loading: boolean
    icon: IconDefinition
    leaderboard: ILeaderboardResult[]
    metric: string
    timePeriod: string
    csatAsPercent: boolean
    isFiveScore: boolean
    decimals: {
        nps: number
        csat: number
        fivestar: number
        ces: number
    }
    showError?: boolean
    onReloadClick?: Function
    rowsToShow?: number
}
const props = withDefaults(defineProps<Props>(), {
    showError: false,
    onReloadClick: () => ({}),
    rowsToShow: 8,
})

const loadingRows = Array.from({ length: props.rowsToShow }, () => ({}))

const rankedLeaderboard = computed(() => {
    return (
        props.leaderboard?.map((item, index) => ({
            ...item,
            rank: index + 1,
        })) ?? []
    )
})

const dataTablePt = {
    bodyRow: { style: { background: 'unset' } },
    thead: { style: { zIndex: 0 } },
}

const tableScrollHeight = computed(() => {
    const headerHeight = 41
    const rowHeight = 57
    return `${props.rowsToShow * rowHeight + headerHeight}px`
})

const showResponsesColumn = computed(
    () => window.innerWidth >= tabletBreakpoint
)
</script>

<style lang="less">
.fm-table {
    .p-datatable-table-container {
        scrollbar-width: thin;
    }
}
</style>
